import React, { useState, useEffect } from "react";
import Header from "./Header";
import Footer from "./Footer";
import WhatsAppWidget from "./WhatsAppWidget";
import { Phone, Mail, User, Building, Smartphone, Send } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const PreventivoPage = () => {
  const { toast } = useToast();
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    nome_cognome: '',
    email: '',
    telefono: '',
    piva: '',
    servizio_interesse: '',
    messaggio: '',
    // Anti-bot honeypot (hidden field)
    website_url: '',
    company_website: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [formStartTime] = useState(Date.now());

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // ===== PROTEZIONE ANTI-BOT =====
      
      // 1. Honeypot check - se compilato = bot
      if (formData.website_url.trim() !== '' || formData.company_website.trim() !== '') {
        console.log('🚫 Bot detected: honeypot filled');
        toast({
          variant: "destructive",
          title: "Errore",
          description: "Richiesta non valida. Riprova tra qualche minuto.",
        });
        setIsLoading(false);
        return;
      }

      // 2. Timing check - troppo veloce = bot
      const formDuration = Date.now() - formStartTime;
      if (formDuration < 5000) { // Minimo 5 secondi
        console.log(`🚫 Bot detected: too fast (${formDuration}ms)`);
        toast({
          variant: "destructive",
          title: "Errore",
          description: "Compila il form con più attenzione. Attendi almeno 5 secondi.",
        });
        setIsLoading(false);
        return;
      }

      // 3. Basic validation
      if (!formData.nome_cognome || formData.nome_cognome.length < 2) {
        toast({
          variant: "destructive",
          title: "Errore",
          description: "Inserisci nome e cognome validi (minimo 2 caratteri).",
        });
        setIsLoading(false);
        return;
      }

      if (!formData.email || !formData.email.includes('@')) {
        toast({
          variant: "destructive",
          title: "Errore",
          description: "Inserisci un indirizzo email valido.",
        });
        setIsLoading(false);
        return;
      }

      if (!formData.telefono || formData.telefono.length < 8) {
        toast({
          variant: "destructive",
          title: "Errore",
          description: "Inserisci un numero di telefono valido (minimo 8 cifre).",
        });
        setIsLoading(false);
        return;
      }

      // 4. Content validation - controlla spam nel nome e messaggio
      const spamKeywords = ['http', 'www', '.com', '.net', 'crypto', 'bitcoin', 'investment', 'loan', 'casino', 'viagra'];
      const textToCheck = `${formData.nome_cognome} ${formData.messaggio}`.toLowerCase();
      
      if (spamKeywords.some(keyword => textToCheck.includes(keyword))) {
        console.log('🚫 Spam content detected');
        toast({
          variant: "destructive",
          title: "Errore",
          description: "Contenuto non valido rilevato. Controlla i dati inseriti.",
        });
        setIsLoading(false);
        return;
      }

      // Prepare clean data (remove honeypot fields)
      const cleanData = {
        nome_cognome: formData.nome_cognome.trim(),
        email: formData.email.trim(),
        telefono: formData.telefono.trim(),
        piva: formData.piva?.trim() || '',
        servizio_interesse: formData.servizio_interesse || '',
        messaggio: formData.messaggio?.trim() || '',
        // Add anti-bot metadata
        form_duration: formDuration,
        submit_timestamp: Date.now()
      };

      // Get backend URL from environment
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/quote-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cleanData),
      });

      if (!response.ok) {
        throw new Error('Errore nell\'invio della richiesta');
      }

      const result = await response.json();
      
      toast({
        title: "Preventivo inviato con successo!",
        description: "Ti contatteremo entro 24 ore per fornirti il preventivo personalizzato.",
      });

      // Reset form
      setFormData({
        nome_cognome: '',
        email: '',
        telefono: '',
        piva: '',
        servizio_interesse: '',
        messaggio: '',
        // Anti-bot honeypot (hidden field)
        website_url: '',
        company_website: ''
      });

    } catch (error) {
      toast({
        title: "Errore nell'invio",
        description: "Si è verificato un errore. Riprova o contattaci direttamente.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-black text-white py-32 overflow-hidden" 
        style={{ paddingTop: '220px' }}
      >
        {/* Futuristic Background Image */}
        <div 
          className="absolute inset-0 z-0 opacity-40 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: "url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072&auto=format&fit=crop')",
          }}
        />
        {/* Dark Gradient Overlay */}
        <div className="absolute inset-0 z-1 bg-gradient-to-b from-black/70 via-black/50 to-gray-900" />

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 tracking-tight">
              Richiedi il tuo <span className="text-orange-500">Preventivo</span>
            </h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
              Compila il form sottostante e riceverai un preventivo personalizzato 
              entro 24 ore. I nostri esperti ti contatteranno per offrirti la soluzione migliore.
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="bg-gray-900 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12">
              {/* Form */}
              <div className="bg-gray-800 rounded-lg p-8 shadow-xl border border-gray-700">
                <h2 className="text-2xl font-bold text-white mb-8">
                  Dati per il Preventivo
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-gray-300 text-sm font-medium mb-2">
                      Nome e Cognome *
                    </label>
                    <input
                      type="text"
                      name="nome_cognome"
                      value={formData.nome_cognome}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-orange-500 focus:outline-none transition-colors"
                      placeholder="Mario Rossi"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-300 text-sm font-medium mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-orange-500 focus:outline-none transition-colors"
                        placeholder="mario.rossi@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 text-sm font-medium mb-2">
                        Telefono *
                      </label>
                      <input
                        type="tel"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-orange-500 focus:outline-none transition-colors"
                        placeholder="+39 333 1234567"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-300 text-sm font-medium mb-2">
                        Partita IVA (Opzionale)
                      </label>
                      <input
                        type="text"
                        name="piva"
                        value={formData.piva}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-orange-500 focus:outline-none transition-colors"
                        placeholder="IT01234567890"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 text-sm font-medium mb-2">
                        Servizio di Interesse (Opzionale)
                      </label>
                      <select
                        name="servizio_interesse"
                        value={formData.servizio_interesse}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-orange-500 focus:outline-none transition-colors"
                      >
                        <option value="">Seleziona un servizio</option>
                        <option value="Super Smart 100">Super Smart 100 - Mobile</option>
                        <option value="Super Unlimited">Super Unlimited - Mobile</option>
                        <option value="Super Unlimited +">Super Unlimited + - Mobile</option>
                        <option value="Super Office One">Super Office One - Fibra</option>
                        <option value="Office Data">Office Data - Fibra</option>
                        <option value="Offerte Office">Offerte Office - Fibra</option>
                        <option value="Office Smart">Office Smart - Centralino</option>
                        <option value="Office Smart Power">Office Smart Power - Centralino</option>
                        <option value="Connettività">Connettività</option>
                        <option value="Machine2Machine">Machine2Machine - IoT</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-300 text-sm font-medium mb-2">
                      Messaggio (Opzionale)
                    </label>
                    <textarea
                      name="messaggio"
                      value={formData.messaggio}
                      onChange={handleChange}
                      rows={3}
                      className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-orange-500 focus:outline-none transition-colors"
                      placeholder="Descrivi le tue esigenze specifiche..."
                    />
                  </div>

                  {/* Honeypot fields - hidden from users but visible to bots */}
                  <div style={{ display: 'none' }}>
                    <input
                      type="text"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleChange}
                      tabIndex="-1"
                      autoComplete="off"
                      placeholder="Leave this field empty"
                    />
                    <input
                      type="text"
                      name="company_website"
                      value={formData.company_website}
                      onChange={handleChange}
                      tabIndex="-1"
                      autoComplete="off"
                      placeholder="Do not fill this field"
                    />
                  </div>

                  {/* Honeypot fields - hidden from users but visible to bots */}
                  <div style={{ display: 'none', position: 'absolute', left: '-9999px', visibility: 'hidden' }}>
                    <label htmlFor="website_url">Sito web (non compilare):</label>
                    <input
                      type="text"
                      id="website_url"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleChange}
                      tabIndex="-1"
                      autoComplete="off"
                      placeholder="Leave empty"
                    />
                    <label htmlFor="company_website">Website azienda (lascia vuoto):</label>
                    <input
                      type="text"
                      id="company_website"
                      name="company_website"
                      value={formData.company_website}
                      onChange={handleChange}
                      tabIndex="-1"
                      autoComplete="off"
                      placeholder="Do not fill"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-gray-600 text-black font-bold py-4 px-8 rounded-lg transition-colors duration-200 flex items-center justify-center shadow-lg"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black mr-2"></div>
                        Invio in corso...
                      </>
                    ) : (
                      <>
                        <Send size={20} className="mr-2" />
                        Invia Richiesta Preventivo
                      </>
                    )}
                  </button>
                </form>
              </div>

              {/* Info Panel */}
              <div className="space-y-8">
                <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 shadow-xl">
                  <h3 className="text-xl font-bold text-white mb-4">
                    Perché scegliere Lovecchio Group?
                  </h3>
                  <ul className="space-y-3 text-gray-300">
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-orange-400 rounded-full mr-3 mt-2"></div>
                      Oltre 10 anni di esperienza nel settore
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-orange-400 rounded-full mr-3 mt-2"></div>
                      Partner ufficiale WindTre Business
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-orange-400 rounded-full mr-3 mt-2"></div>
                      Assistenza dedicata H24
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-orange-400 rounded-full mr-3 mt-2"></div>
                      Soluzioni personalizzate per ogni business
                    </li>
                  </ul>
                </div>

                <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 shadow-xl">
                  <h3 className="text-xl font-bold text-white mb-4">Contatti Diretti</h3>
                  <div className="space-y-3">
                    <div className="flex items-center text-gray-300">
                      <Phone size={16} className="mr-3 text-orange-400" />
                      <span>+39 333 1341439</span>
                    </div>
                    <div className="flex items-center text-gray-300">
                      <Mail size={16} className="mr-3 text-orange-400" />
                      <span>info@lovecchiogroup.it</span>
                    </div>
                    <div className="flex items-center text-gray-300">
                      <Building size={16} className="mr-3 text-orange-400" />
                      <span>Via Fiesole 50, Lecce</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-6 text-center shadow-xl">
                  <h3 className="text-xl font-bold text-black mb-2">
                    Risposta Garantita
                  </h3>
                  <p className="text-black font-medium">
                    Ti contatteremo entro <strong>24 ore</strong> dalla richiesta
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppWidget />
    </div>
  );
};

export default PreventivoPage;
